<script setup lang="ts">
import { useSnackbarStore } from '@/store/snackbar'
import { $updateWorkOrder, $workOrder, $workOrderOptions } from '@/api/brandings'
import { can } from '@layouts/plugins/casl'

interface IProps {
  modelValue?: boolean,
  dialogTitle?: string,
  workOrderId?: number | string | undefined,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  dialogTitle: 'Work Order',
  workOrderId: undefined,
})

interface Emit {
  (e: 'update:modelValue', value: boolean): void
  (e: 'onSubmit'): void
  (e: 'onClose'): void
}

const emit = defineEmits<Emit>()

const route = useRoute()

const snackbarStore = useSnackbarStore()

const workOrder = ref<any>({})

const category1Options = ref<{ value: number, display_name: string }[]>([])
const category2Options = ref<{ value: number, display_name: string }[]>([])

const loading = ref<boolean>(false)
const workOrderOptionsLoading = ref<boolean>(false)
const submitLoading = ref<boolean>(false)

const dialogSubtitle = ref<string>('')

const isActiveProject = computed(() => {
  return workOrder.value?.is_visible_campaign || false
})

const getWorkOrder = async () => {
  if (!props.workOrderId) return

  loading.value = true
  try {
    const res = await $workOrder(props.workOrderId)
    workOrder.value = res.data

    dialogSubtitle.value = workOrder.value.order_no
  } catch (e) {
    console.log(e)
  } finally {
    loading.value = false
  }
}

const updateWorkOrder = async () => {
  if (!props.workOrderId) return

  const payload = {
    category_1: workOrder.value?.category_1 || null,
    category_2: workOrder.value?.category_2 || null,
    budget: workOrder.value?.budget || 0
  }

  submitLoading.value = true
  try {
    const res = await $updateWorkOrder(props.workOrderId, payload)
    workOrder.value = res.data
  } catch (e) {
    console.log(e)
  } finally {
    emit('onSubmit')
    hideDialog()
    submitLoading.value = false
  }
}

const getWorkOrderOptions = async () => {
  workOrderOptionsLoading.value = true
  try {
    const res = await $workOrderOptions()
    category1Options.value = res.data.actions.POST.category_1.choices
    category2Options.value = res.data.actions.POST.category_2.choices
  }
  catch (e) {
    console.log(e)
  } finally {
    workOrderOptionsLoading.value = false
  }
}

const hideDialog = () => {
  emit('update:modelValue', false)
  onClose()
}

const onClose = async () => {
  emit('onClose')
}

const onSubmit = async () => {
  emit('onSubmit')
}

watch(() => props.modelValue, () => {
  // on show Dialog
  if (!!props.modelValue) {
    getWorkOrder()
    getWorkOrderOptions()
  }
  // on hide Dialog
  else {
    onClose()
  }
})
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="600"
    :persistent="!!loading || !!submitLoading"
    persistent
    @after-leave="onClose"
    @update:modelValue="emit('update:modelValue')"
  >
    <DialogCloseBtn
      :disabled="!!loading || !!submitLoading"
      @click="hideDialog"
    />

    <VCard>
      <template #title>
        {{ dialogTitle }}
      </template>
      <template #subtitle>
        {{ dialogSubtitle }}
      </template>
      <VCardText>
        <VContainer
          fluid
          class="position-relative"
        >
          <VOverlay
            v-model="loading"
            persistent
            absolute
            contained
            class="align-center justify-center"
          >
            <VProgressCircular
              indeterminate
              color="primary"
            />
          </VOverlay>
          <VRow>
            <VCol cols="12">
              <VSelect
                v-model="workOrder.category_1"
                :items="category1Options"
                :loading="!!workOrderOptionsLoading"
                :disabled="!!submitLoading || !isActiveProject"
                label="Category 1"
                item-value="value"
                item-title="display_name"
              />
            </VCol>
            <VCol cols="12">
              <VSelect
                v-model="workOrder.category_2"
                :items="category2Options"
                :loading="!!workOrderOptionsLoading"
                :disabled="!!submitLoading || !isActiveProject"
                label="Category 2"
                item-value="value"
                item-title="display_name"
              />
            </VCol>
            <VCol
              v-if="can('view', 'finances')"
              cols="12"
            >
              <VTextField
                v-model="workOrder.budget"
                :disabled="!!submitLoading || !isActiveProject"
                label="Budget"
                type="number"
                step="1"
                min="0"
              />
            </VCol>
          </VRow>
          <VRow>
            <VCol class="d-flex justify-space-between align-center">
              <VBtn
                :disabled="!!submitLoading"
                class="pa-1"
                color="secondary"
                @click="hideDialog"
              >
                Cancel
              </VBtn>
              <VBtn
                :disabled="!!submitLoading || !!loading || !isActiveProject"
                class="pa-1"
                @click="updateWorkOrder"
              >
                Save
                <VProgressCircular
                  v-if="submitLoading"
                  class="ml-1"
                  size="16"
                  width="2"
                  indeterminate
                  color="white"
                />
              </VBtn>
            </VCol>
          </VRow>
        </VContainer>
      </VCardText>
    </VCard>
  </VDialog>
</template>
